import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Trash2, UserPlus } from 'lucide-react';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { barberInput, barberServicesInput } from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Field } from '@/components/shared/field';
import { MaskedInput } from '@/components/shared/masked-input';
import { PageHeader } from '@/components/shared/page-header';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage } from '@/lib/api';
import { centsToInput, money, parseMoney } from '@/lib/format';
import { useBranches } from '@/lib/org';
import { useSession } from '@/lib/session';
import { HoursEditor } from '../settings/hours-editor';
import { InviteModal } from '../settings/invite-modal';
import { useAllServices, useChairs, type BarberServiceItem, type Member } from './api';
import { ColorField } from './color-field';
import { ImageUploadField } from './image-upload';
import { TagsInput } from './tags-input';
import { applyServerError, zodForm } from './lib';
import { BarberAvatar } from './team-page';

interface Barber {
  id: string;
  displayName: string;
  bio: string | null;
  specialties: string[];
  photoAssetId: string | null;
  branchId: string;
  chairId: string | null;
  color: string;
  monthlyGoalCents: number | null;
  active: boolean;
  publicVisible: boolean;
}

const TABS = ['perfil', 'servicos', 'horarios', 'acesso'] as const;

export function BarberPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useSession();
  const [tab, setTab] = useQueryState('aba', parseAsStringLiteral(TABS).withDefault('perfil'));
  const barber = useQuery({ queryKey: ['barbers', 'one', id], queryFn: () => api.get<Barber>(`/barbers/${id}`) });
  const canEdit = can('catalog.manage');

  const del = async () => {
    try {
      await api.del(`/barbers/${id}`);
      toast.success('Profissional excluído');
      qc.invalidateQueries({ queryKey: ['barbers'] });
      navigate('/app/equipe');
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  if (barber.isLoading) return <LoadingState />;
  if (barber.error || !barber.data) return <ErrorState error={barber.error} onRetry={() => barber.refetch()} />;
  const b = barber.data;

  return (
    <div>
      <Button variant="ghost" size="sm" asChild className="-ml-2 mb-2">
        <Link to="/app/equipe">
          <ArrowLeft aria-hidden /> Equipe
        </Link>
      </Button>
      <PageHeader
        title={b.displayName}
        description="Ficha do profissional."
        actions={
          <>
            {can('commissions.manage') && (
              <Button variant="outline" asChild>
                <Link to="/app/comissoes">Regras de comissão</Link>
              </Button>
            )}
            {canEdit && (
              <ConfirmDialog
                trigger={
                  <Button variant="outline">
                    <Trash2 className="text-destructive" aria-hidden /> Excluir profissional
                  </Button>
                }
                title={`Excluir ${b.displayName}?`}
                description="Ele deixa de aparecer na agenda e no site, e os atendimentos já feitos são mantidos. Dá para restaurar depois em Equipe, na lista de excluídos."
                confirmLabel="Excluir profissional"
                onConfirm={del}
              />
            )}
          </>
        }
      />
      <Tabs value={tab} onValueChange={(v) => setTab(v as (typeof TABS)[number])}>
        <TabsList className="h-auto flex-wrap justify-start">
          <TabsTrigger value="perfil">Perfil</TabsTrigger>
          <TabsTrigger value="servicos">Serviços</TabsTrigger>
          <TabsTrigger value="horarios">Horários</TabsTrigger>
          <TabsTrigger value="acesso">Acesso</TabsTrigger>
        </TabsList>
        <TabsContent value="perfil" className="pt-4">
          <ProfileTab barber={b} canEdit={canEdit} />
        </TabsContent>
        <TabsContent value="servicos" className="pt-4">
          <ServicesTab barberId={b.id} canEdit={canEdit} />
        </TabsContent>
        <TabsContent value="horarios" className="pt-4">
          <p className="mb-4 max-w-prose text-sm text-muted-foreground">
            Sem horário próprio, vale o horário de funcionamento da unidade. Defina um horário individual só se este profissional trabalha em dias ou horas diferentes.
          </p>
          {canEdit ? <HoursEditor branchId={b.branchId} barberId={b.id} /> : <p className="text-sm">Você não tem permissão para alterar horários.</p>}
        </TabsContent>
        <TabsContent value="acesso" className="pt-4">
          <AccessTab barber={b} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------- Perfil ----------------
interface ProfileValues {
  displayName: string;
  bio: string;
  specialties: string[];
  photoAssetId: string | null;
  branchId: string;
  chairId: string;
  color: string;
  goal: string;
  active: boolean;
  publicVisible: boolean;
}
const NONE = 'none';
const toPayload = (v: ProfileValues) => ({
  displayName: v.displayName,
  bio: v.bio,
  specialties: v.specialties,
  photoAssetId: v.photoAssetId,
  branchId: v.branchId,
  chairId: v.chairId === NONE ? null : v.chairId,
  active: v.active,
  publicVisible: v.publicVisible,
  monthlyGoalCents: parseMoney(v.goal) > 0 ? parseMoney(v.goal) : null,
  color: v.color,
});

function ProfileTab({ barber, canEdit }: { barber: Barber; canEdit: boolean }) {
  const qc = useQueryClient();
  const branches = useBranches();
  const form = useForm<ProfileValues>({ resolver: zodForm<ProfileValues>(barberInput, toPayload, { monthlyGoalCents: 'goal' }) });
  const { errors, isDirty } = form.formState;
  const branchId = form.watch('branchId');
  const chairs = useChairs(branchId);

  useEffect(() => {
    form.reset({
      displayName: barber.displayName,
      bio: barber.bio ?? '',
      specialties: barber.specialties,
      photoAssetId: barber.photoAssetId,
      branchId: barber.branchId,
      chairId: barber.chairId ?? NONE,
      color: barber.color,
      goal: barber.monthlyGoalCents ? centsToInput(barber.monthlyGoalCents) : '',
      active: barber.active,
      publicVisible: barber.publicVisible,
    });
  }, [barber]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (v: ProfileValues) => api.put(`/barbers/${barber.id}`, barberInput.parse(toPayload(v))),
    onSuccess: () => {
      toast.success('Profissional salvo');
      qc.invalidateQueries({ queryKey: ['barbers'] });
      qc.invalidateQueries({ queryKey: ['agenda'] });
    },
    onError: (e) => applyServerError(form, e, { monthlyGoalCents: 'goal' }),
  });

  const color = form.watch('color');
  const name = form.watch('displayName');

  return (
    <form onSubmit={form.handleSubmit((v) => save.mutate(v))} className="grid max-w-3xl gap-5" noValidate>
      <fieldset disabled={!canEdit} className="grid gap-5">
        <div className="flex items-center gap-3">
          <BarberAvatar b={{ displayName: name || '?', photoAssetId: form.watch('photoAssetId'), color }} className="size-14" />
          <p className="text-sm text-muted-foreground">É assim que ele aparece na agenda, com a cor escolhida.</p>
        </div>
        <Field id="bp-name" label="Nome de exibição" error={errors.displayName?.message}>
          <Input id="bp-name" aria-invalid={!!errors.displayName} {...form.register('displayName')} />
        </Field>
        <Field id="bp-bio" label="Bio (opcional)" error={errors.bio?.message} hint="Texto curto exibido no site.">
          <Textarea id="bp-bio" rows={3} maxLength={1000} {...form.register('bio')} />
        </Field>
        <Field id="bp-spec" label="Especialidades" error={errors.specialties?.message as string | undefined} hint="Tecle Enter para adicionar. Ex.: degradê, barba, pigmentação.">
          <Controller control={form.control} name="specialties" render={({ field }) => <TagsInput id="bp-spec" value={field.value ?? []} onChange={field.onChange} />} />
        </Field>
        <Controller
          control={form.control}
          name="photoAssetId"
          render={({ field }) => <ImageUploadField value={field.value} onChange={(v) => field.onChange(v)} purpose="barber" label="Foto" defaultAlt={`Foto de ${name}`} />}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="bp-branch" label="Unidade" error={errors.branchId?.message}>
            <Controller
              control={form.control}
              name="branchId"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={(v) => {
                    field.onChange(v);
                    form.setValue('chairId', NONE, { shouldDirty: true });
                  }}
                >
                  <SelectTrigger id="bp-branch" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.data?.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
          <Field id="bp-chair" label="Cadeira" error={errors.chairId?.message}>
            <Controller
              control={form.control}
              name="chairId"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="bp-chair" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Sem cadeira fixa</SelectItem>
                    {chairs.data?.filter((c) => c.active || c.id === barber.chairId).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
          <Field id="bp-goal" label="Meta mensal (opcional)" error={errors.goal?.message} hint="Faturamento esperado por mês, usado nos relatórios.">
            <MaskedInput mask="money" id="bp-goal" {...form.register('goal')} />
          </Field>
          <Controller control={form.control} name="color" render={({ field }) => <ColorField id="bp-color" label="Cor na agenda" value={field.value} onChange={field.onChange} error={errors.color?.message} />} />
        </div>
        <div className="grid gap-3 rounded-md border p-3">
          <Controller
            control={form.control}
            name="publicVisible"
            render={({ field }) => (
              <label className="flex items-center justify-between gap-3 text-sm">
                <span>
                  <span className="font-medium">Visível no site</span>
                  <span className="block text-xs text-muted-foreground">Clientes podem escolher este profissional ao agendar online.</span>
                </span>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </label>
            )}
          />
          <Controller
            control={form.control}
            name="active"
            render={({ field }) => (
              <label className="flex items-center justify-between gap-3 text-sm">
                <span>
                  <span className="font-medium">Ativo</span>
                  <span className="block text-xs text-muted-foreground">Inativo some da agenda para novos agendamentos.</span>
                </span>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </label>
            )}
          />
        </div>
      </fieldset>
      {canEdit && (
        <div>
          <Button type="submit" disabled={save.isPending || !isDirty}>
            {save.isPending ? 'Salvando…' : 'Salvar profissional'}
          </Button>
        </div>
      )}
    </form>
  );
}

// ---------------- Serviços ----------------
interface Row {
  enabled: boolean;
  price: string;
  duration: string;
}

function ServicesTab({ barberId, canEdit }: { barberId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const services = useAllServices();
  const current = useQuery({ queryKey: ['barbers', 'services', barberId], queryFn: () => api.get<BarberServiceItem[]>(`/barbers/${barberId}/services`) });
  const [rows, setRows] = useState<Record<string, Row>>({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!current.data) return;
    setRows(
      Object.fromEntries(
        current.data.map((i) => [
          i.serviceId,
          { enabled: true, price: i.priceCentsOverride != null ? centsToInput(i.priceCentsOverride) : '', duration: i.durationMinutesOverride != null ? String(i.durationMinutesOverride) : '' },
        ]),
      ),
    );
    setDirty(false);
  }, [current.data]);

  const patch = (id: string, p: Partial<Row>) => {
    setRows((r) => ({ ...r, [id]: { ...(r[id] ?? { enabled: false, price: '', duration: '' }), ...p } }));
    setDirty(true);
  };

  const save = useMutation({
    mutationFn: () => {
      const items = Object.entries(rows)
        .filter(([, r]) => r.enabled)
        .map(([serviceId, r]) => ({
          serviceId,
          priceCentsOverride: r.price ? parseMoney(r.price) : null,
          durationMinutesOverride: r.duration ? Number(r.duration) : null,
        }));
      return api.put(`/barbers/${barberId}/services`, barberServicesInput.parse({ items }));
    },
    onSuccess: () => {
      toast.success('Serviços salvos');
      qc.invalidateQueries({ queryKey: ['barbers'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (services.isLoading || current.isLoading) return <LoadingState />;
  if (services.error || current.error) return <ErrorState error={services.error ?? current.error} onRetry={() => { services.refetch(); current.refetch(); }} />;
  if (!services.data?.length)
    return (
      <p className="text-sm text-muted-foreground">
        Nenhum serviço ativo ainda. <Link className="underline" to="/app/servicos">Cadastre os serviços</Link> para habilitar aqui.
      </p>
    );

  const enabledCount = Object.values(rows).filter((r) => r.enabled).length;

  return (
    <div className="grid max-w-4xl gap-4">
      <p className="text-sm text-muted-foreground">
        Marque os serviços que este profissional atende. Preço e duração são opcionais: se ficarem em branco, vale o padrão do serviço.
      </p>
      <ul className="divide-y rounded-lg border bg-card">
        {services.data.map((s) => {
          const r = rows[s.id] ?? { enabled: false, price: '', duration: '' };
          return (
            <li key={s.id} className="grid gap-3 p-3 sm:grid-cols-[1fr_9rem_9rem] sm:items-center">
              <label className="flex items-center gap-3">
                <Checkbox checked={r.enabled} disabled={!canEdit} onCheckedChange={(v) => patch(s.id, { enabled: v === true })} />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">{s.name}</span>
                  <span className="tabular text-xs text-muted-foreground">
                    Padrão: {money(s.priceCents)} · {s.durationMinutes} min
                  </span>
                </span>
              </label>
              <div>
                <label htmlFor={`p-${s.id}`} className="sr-only">
                  Preço de {s.name} para este profissional
                </label>
                <MaskedInput mask="money" id={`p-${s.id}`} placeholder={centsToInput(s.priceCents)} disabled={!r.enabled || !canEdit} value={r.price} onChange={(e) => patch(s.id, { price: e.target.value === centsToInput(0) ? '' : e.target.value })} className="tabular" />
              </div>
              <div>
                <label htmlFor={`d-${s.id}`} className="sr-only">
                  Duração de {s.name} para este profissional, em minutos
                </label>
                <Input id={`d-${s.id}`} type="number" min={5} max={480} step={5} placeholder={`${s.durationMinutes} min`} disabled={!r.enabled || !canEdit} value={r.duration} onChange={(e) => patch(s.id, { duration: e.target.value })} className="tabular" />
              </div>
            </li>
          );
        })}
      </ul>
      {canEdit && (
        <div className="flex items-center gap-3">
          <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
            {save.isPending ? 'Salvando…' : 'Salvar serviços'}
          </Button>
          <span className="text-sm text-muted-foreground">{enabledCount} habilitados</span>
        </div>
      )}
    </div>
  );
}

// ---------------- Acesso ----------------
function AccessTab({ barber }: { barber: Barber }) {
  const qc = useQueryClient();
  const { can } = useSession();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [linkId, setLinkId] = useState('');
  const allowed = can('members.manage');
  const members = useQuery({ queryKey: ['org', 'members'], queryFn: () => api.get<Member[]>('/org/members'), enabled: allowed });

  const link = useMutation({
    mutationFn: (memberId: string) => api.patch(`/org/members/${memberId}`, { barberId: barber.id }),
    onSuccess: () => {
      toast.success('Acesso vinculado');
      setLinkId('');
      qc.invalidateQueries({ queryKey: ['org', 'members'] });
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (!allowed) return <p className="text-sm text-muted-foreground">Só quem gerencia membros e papéis vê e altera o acesso ao sistema.</p>;
  if (members.isLoading) return <LoadingState />;
  if (members.error) return <ErrorState error={members.error} onRetry={() => members.refetch()} />;

  const linked = members.data?.find((m) => m.barberId === barber.id);
  const free = members.data?.filter((m) => !m.barberId && m.active) ?? [];

  return (
    <div className="grid max-w-2xl gap-5">
      {linked ? (
        <div className="rounded-lg border bg-card p-4">
          <p className="text-sm text-muted-foreground">Este profissional já tem usuário no sistema.</p>
          <p className="mt-1 font-medium">{linked.name}</p>
          <p className="text-sm text-muted-foreground">{linked.email}</p>
          <div className="mt-2 flex gap-1.5">
            <Badge variant="secondary">{linked.roleName}</Badge>
            <Badge variant={linked.active ? 'secondary' : 'outline'}>{linked.active ? 'Ativo' : 'Desativado'}</Badge>
          </div>
          <Button variant="outline" size="sm" className="mt-3" asChild>
            <Link to="/app/configuracoes/equipe">Gerenciar em Equipe e permissões</Link>
          </Button>
        </div>
      ) : (
        <>
          <div className="rounded-lg border bg-card p-4">
            <p className="font-medium">Sem acesso ao sistema</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Com um usuário vinculado, ele vê a própria agenda e comissões, conforme o papel. Quem recebe o convite cria a senha e já entra com o papel Barbeiro.
            </p>
            <Button className="mt-3" onClick={() => setInviteOpen(true)}>
              <UserPlus aria-hidden /> Convidar para acessar a agenda
            </Button>
          </div>
          {free.length > 0 && (
            <div className="rounded-lg border bg-card p-4">
              <p className="font-medium">Vincular um usuário que já existe</p>
              <div className="mt-3 flex flex-wrap items-end gap-2">
                <div className="grid gap-1.5">
                  <label htmlFor="link-member" className="text-sm font-medium">
                    Membro da equipe
                  </label>
                  <Select value={linkId} onValueChange={setLinkId}>
                    <SelectTrigger id="link-member" className="w-64">
                      <SelectValue placeholder="Escolha um membro" />
                    </SelectTrigger>
                    <SelectContent>
                      {free.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.name} ({m.roleName})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" disabled={!linkId || link.isPending} onClick={() => link.mutate(linkId)}>
                  Vincular usuário
                </Button>
              </div>
            </div>
          )}
        </>
      )}
      <InviteModal open={inviteOpen} onOpenChange={setInviteOpen} defaultRoleKey="barber" defaultBarberId={barber.id} lockBarber />
    </div>
  );
}
